/**
 * Team kit COLOURS — the one piece of the deleted character system worth
 * keeping.
 *
 * The old `kit-shader.ts` was a character renderer: a GPU program that posed a
 * segmented body, tinted colour zones and stamped decal numbers. All of that is
 * gone with the rest of the character pipeline. But `deriveKit` was never
 * really part of it — it is a pure function from a team id to three linear
 * colours, and the HUD, the menu and any future player visual all need to agree
 * on what "home red" and "away blue" are.
 *
 * So it moves here, unchanged in behaviour, with no Three.js and no rendering
 * in sight. Nothing in this file knows what a player looks like.
 *
 * SIGNATURE IS FROZEN — hud.ts, menu.ts and the debug player visual consume it.
 * Change the values if you like; do not change the shape.
 */

/** Linear RGB, 0..1. */
export interface KitColors {
  primary: [number, number, number];
  secondary: [number, number, number];
  accent: [number, number, number];
}

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

const linTriple = (rgb: [number, number, number]): [number, number, number] => [
  srgbToLinear(rgb[0]),
  srgbToLinear(rgb[1]),
  srgbToLinear(rgb[2]),
];

/** HSL→sRGB (all 0..1). Local, so kit generation owns its colour math. */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const hue2rgb = (p: number, q: number, t: number): number => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue2rgb(p, q, h + 1 / 3), hue2rgb(p, q, h), hue2rgb(p, q, h - 1 / 3)];
}

/**
 * Derive a full kit from a team id. The two MATCH teams are hand-tuned,
 * broadcast-locked strips: live captures showed an earlier derived pair as two
 * similar blue-ish kits that were hard to tell apart at distance. Any further
 * team id falls back to the golden-ratio hue walk, so this is still not a
 * two-team special case.
 *
 * Colours are FICTIONAL (§16 — no real-world club identities) and LINEAR.
 *
 *   0 HOME: red shirt / white shorts / red accent.
 *   1 AWAY: royal-blue shirt / navy shorts / sky accent.
 */
export function deriveKit(teamId: number): KitColors {
  if (teamId === 0) {
    return {
      primary: linTriple([0.78, 0.08, 0.12]), // deep saturated red
      secondary: linTriple([0.95, 0.95, 0.96]), // white
      // Scarlet, deliberately LIGHTER than the shirt so ink still separates.
      accent: linTriple([1.0, 0.34, 0.27]),
    };
  }
  if (teamId === 1) {
    return {
      primary: linTriple([0.1, 0.28, 0.82]), // royal blue
      secondary: linTriple([0.06, 0.1, 0.28]), // navy
      accent: linTriple([0.52, 0.78, 1.0]), // sky
    };
  }
  // Teams 2+: golden-ratio spaced hue walk generalises to any N teams.
  const hue = (0.58 + teamId * (0.5 + 0.61803398875)) % 1;
  return {
    primary: linTriple(hslToRgb(hue, 0.62, 0.44)),
    secondary: linTriple(hslToRgb(hue, 0.5, 0.86)),
    accent: linTriple(hslToRgb((hue + 0.5) % 1, 0.7, 0.55)),
  };
}

/** Controlled-player rim highlight, linear. */
export const HIGHLIGHT_COLOR = linTriple([1.0, 0.82, 0.28]);
